/**
 * Recovered sign-in leg attribution — which "Recovered · no UPN" legs are the attacker's.
 *
 * Plan: Plans/2026-09-30_Recovered-Legs-Transaction-Attribution.md (rev 3), sections 2.2–2.3.
 *
 * 🔴 `UnattributedRecovery` is NOT an AiTM signal. It means Microsoft logged the row with
 * `UserId = "Not Available"` and the runbook recovered it by the user's object id — which is
 * Microsoft's normal logging for MFA sub-steps (`SAS:BeginAuth` / `SAS:EndAuth` /
 * `SAS:ProcessAuth`; 84 % of `SAS:*` legs fleet-wide). Until v1.188.0 every such leg anchored
 * the attacker window, so on IR-20260930-mendozap-001 fifty-two of the victim's own MFA legs,
 * from an address the analyst had marked Safe, put the window start 34 hours early.
 *
 * A leg belongs to a TRANSACTION: the rows sharing its `CorrelationId` (UAL
 * `$.InterSystemsId`, carried by the runbook since 2026-09-30.1). This module decides, for every
 * recovered or URL-shaped leg, in four separate steps:
 *
 *   1. LINK      — `transaction` (exact, by CorrelationId), `neighbour` (legacy blobs only:
 *                  a same-IP row within ±60 s), or none.
 *   2. EVIDENCE  — EXACT facts only. A neighbour link produces NO evidence: a proximity match
 *                  can attach a victim leg to an unrelated session, and evidence is what moves
 *                  the window (review finding R2).
 *   3. VERDICT   — attacker | suspected | benign | unknown.
 *   4. ANCHOR    — only `attacker` anchors; the window applies dismissal and success on top.
 *
 * And separately: CAPTURE. A URL-shaped `ApplicationId` (the kit's landing host) is the only
 * signal that proves the credentials and a session token were captured. Every other kind of
 * evidence may anchor the window but never licenses a capture claim (Decision 9).
 *
 * Pure: no React, no component imports. Deliberately independent of the attacker window so it
 * exists when the window is null — a benign-only case still needs its verdicts to hide the
 * victim's legs under the Safe-IP pill (review finding R1).
 */

/** Evidence codes. Any one makes the leg `attacker`. */
export const EVIDENCE = Object.freeze({
  LEG_IP_MALICIOUS: 'leg_ip_malicious',
  TX_IP_MALICIOUS: 'tx_ip_malicious',
  TX_LOGIN_MULTI_IP: 'tx_login_multi_ip',
  TX_ATTACKER_SESSION: 'tx_attacker_session',
  URL_APP: 'url_app',
});

/** Why a leg could not be resolved. */
export const UNRESOLVED = Object.freeze({
  NO_CORRELATION_ID_NO_NEIGHBOUR: 'no_correlation_id_no_neighbour',
  PLACEHOLDER_CORRELATION_ID: 'placeholder_correlation_id',
  TRANSACTION_WITHOUT_SESSION: 'transaction_without_session',
  NEIGHBOUR_AMBIGUOUS: 'neighbour_ambiguous',
});

/**
 * 🔴 Microsoft's placeholder transaction id. Measured by the pipeline team over 7 days of UAL
 * `signin` (reply 2026-10-01): 5,328 rows across 20 tenants and 178 users, mostly `SAS:EndAuth`.
 * Grouping on it would merge unrelated sign-ins into one "transaction", so it is treated as NO id.
 */
export const PLACEHOLDER_CORRELATION_ID = 'ff000000-0000-0000-0000-000000000000';

/**
 * How far from the leg a row of its transaction may be and still count as the leg's sign-in.
 *
 * 🔴 A transaction id is not one sign-in. The pipeline team measured 785 ids spanning over an
 * hour in 7 days (longest 7.0 days), 189 of them holding two `Login:login` rows more than 10
 * minutes apart; the exhibit's own 81ab62f1 spans 12.5 h. Unbounded, a malicious row hours away
 * in a long-lived id would be "evidence" for an unrelated leg. The relay transaction on the
 * exhibit spans 25 seconds.
 */
export const TX_LEG_WINDOW_SECONDS = 600;

/** Legacy neighbour window, seconds either side of the leg (pipeline handoff §5). */
export const NEIGHBOUR_WINDOW_SECONDS = 60;

/**
 * The relaying proxy's signature: two `Login:login` rows from different addresses within this
 * many seconds, in ONE transaction. Measured relay: 1 s.
 *
 * 🔴 "The transaction spans more than one address" is NOT the signature. The victim's own
 * transaction 81ab62f1 on the exhibit re-run holds 11 legs over 12.5 hours across their phone
 * AND their home address — a long-lived transaction refreshes across networks — and the broad
 * rule would have put the window start back at the exact wrong instant the handoff reported
 * (Decision 12). The narrow form fails that control, correctly.
 */
export const LOGIN_MULTI_IP_WINDOW_SECONDS = 5;

const URL_APP_RE = /^https?:\/\//i;

export const isUrlShapedApp = (e) => URL_APP_RE.test(String(e?.ApplicationId || ''));
const isRecovered = (e) => e?.UnattributedRecovery === true;
/** A row this module attributes: a recovered leg, or a phishing-kit landing (URL-shaped app). */
export const isAttributableLeg = (e) => isRecovered(e) || isUrlShapedApp(e);

const toMs = (t) => {
  const ms = Date.parse(t || '');
  return Number.isFinite(ms) ? ms : null;
};

/** The usable transaction id of a row: '' when absent or Microsoft's placeholder. */
export const txIdOf = (r) => {
  const c = String(r?.CorrelationId || '').trim().toLowerCase();
  return c && c !== PLACEHOLDER_CORRELATION_ID ? c : '';
};

const lower = (v) => String(v || '').trim().toLowerCase();
const ipFamily = (ip) => (String(ip).includes(':') ? 6 : 4);

/**
 * Every address this case searched for one person (`IdentityResolution.Identifiers` plus the
 * primary UPN), lowercased. Used so a row logged under the person's other address still counts
 * as theirs.
 */
export function caseIdentityIds(results) {
  const ir = results?.IdentityResolution;
  const ids = new Set();
  for (const id of ir?.Identifiers || []) if (id) ids.add(lower(id));
  if (ir?.PrimaryUpn) ids.add(lower(ir.PrimaryUpn));
  return ids;
}

/**
 * Is this row the same person's as the leg?
 *
 * 🔴 One transaction id can carry two users in one tenant (pipeline team: 160 ids in 7 days,
 * clustered on shared machines — an account switch in one browser). A leg must not inherit
 * another user's session or address through a shared id. A row with no UPN, or a UPN in the
 * case's identity set, counts as the person's — an alias must not split their own transaction.
 */
function sameUser(leg, row, identityIds) {
  const a = lower(leg.UserPrincipalName);
  const b = lower(row.UserPrincipalName);
  if (!a || !b || a === b) return true;
  return identityIds.has(a) && identityIds.has(b);
}

/**
 * The relay signature (Decision 12): two `Login:login` rows from different addresses within
 * 5 s. 🔴 A pair that is one IPv4 and one IPv6 address from the SAME provider is a dual-stack
 * home, not a relay — the pipeline team found exactly that as the only other match fleet-wide in
 * 7 days (fyzical.com, Comporium, Rock Hill). The exhibit's relay is two T-Mobile IPv6 addresses
 * in different cities, so it still counts. An unknown provider on either side still counts.
 */
function hasLoginMultiIp(rows) {
  const logins = [];
  for (const r of rows) {
    if (r.RequestType !== 'Login:login' || !r.IPAddress) continue;
    const ms = toMs(r.EventTimeUTC);
    if (ms !== null) logins.push({ ip: r.IPAddress, ms, provider: lower(r.IP_Provider) });
  }
  for (let i = 0; i < logins.length; i += 1) {
    for (let j = i + 1; j < logins.length; j += 1) {
      const a = logins[i];
      const b = logins[j];
      if (a.ip === b.ip || Math.abs(a.ms - b.ms) > LOGIN_MULTI_IP_WINDOW_SECONDS * 1000) continue;
      const dualStackHome = ipFamily(a.ip) !== ipFamily(b.ip) && a.provider && a.provider === b.provider;
      if (!dualStackHome) return true;
    }
  }
  return false;
}

/**
 * Legacy link for a leg with no CorrelationId: the nearest same-IP row carrying a session,
 * within ±60 s. Tier 1 is the pipeline team's rule (a `Login:login` before the leg or a
 * `SAS:ProcessAuth` after it); tier 2, used only when tier 1 is empty, accepts any same-IP row
 * with a session (Graph-merged rows carry `RequestType = ""`). If the two nearest candidates
 * name different sessions the link is AMBIGUOUS and declined — never guessed.
 */
function findNeighbour(leg, rowsByIp) {
  const legMs = toMs(leg.EventTimeUTC);
  if (legMs === null || !leg.IPAddress) return { status: 'none' };
  const candidates = [];
  for (const r of rowsByIp.get(leg.IPAddress) || []) {
    if (r === leg || !r.SessionId) continue;
    const ms = toMs(r.EventTimeUTC);
    if (ms === null) continue;
    const delta = (ms - legMs) / 1000;
    if (Math.abs(delta) > NEIGHBOUR_WINDOW_SECONDS) continue;
    const tier1 = (r.RequestType === 'Login:login' && delta <= 0)
      || (r.RequestType === 'SAS:ProcessAuth' && delta >= 0);
    candidates.push({ row: r, delta, tier1 });
  }
  if (candidates.length === 0) return { status: 'none' };
  const tier1 = candidates.filter((c) => c.tier1);
  const pool = tier1.length ? tier1 : candidates;
  pool.sort((a, b) => Math.abs(a.delta) - Math.abs(b.delta));
  if (pool.length > 1 && pool[1].row.SessionId !== pool[0].row.SessionId) {
    return { status: 'ambiguous', sessions: [pool[0].row.SessionId, pool[1].row.SessionId] };
  }
  return { status: 'linked', tier: tier1.length ? 1 : 2, pick: pool[0] };
}

/**
 * Attribute every recovered / URL-shaped sign-in leg.
 *
 * @param {Array} signInEvents  ALL interactive sign-in rows, dismissed included — dismissing a
 *                              parent row must never un-resolve its siblings.
 * @param {{ maliciousSet?: Set<string>, attackerSessions?: Set<string>, identityIds?: Set<string> }} ctx
 *   `maliciousSet` = the analyst's malicious addresses; `attackerSessions` = sessions used from
 *   one (buildAttackerContextFrom); `identityIds` = caseIdentityIds(results), the person's
 *   addresses. With no workload context the session evidence cannot fire, but the leg-IP,
 *   transaction-IP, login-multi-IP and URL evidence still apply.
 * @returns {{ byEventId: Map<string, object>, groups: Map<string, object>, counts: object }}
 *   `counts` covers EVERY attributable row, dismissed and failed included. The number that
 *   moves the window (non-dismissed, successful anchors) is on the window result instead.
 *
 * A leg's transaction view is the rows sharing its id that are the SAME person's and within
 * TX_LEG_WINDOW_SECONDS of it (pipeline reply 2026-10-01: ids are reused across users and can
 * span days). A leg whose own time cannot be parsed sees only itself.
 */
export function analyzeRecoveredLegs(signInEvents, {
  maliciousSet = new Set(), attackerSessions = new Set(), identityIds = new Set(),
} = {}) {
  const rows = (signInEvents || []).filter(Boolean);
  const byCid = new Map();
  const rowsByIp = new Map();
  for (const r of rows) {
    const cid = txIdOf(r);
    if (cid) {
      const list = byCid.get(cid) || [];
      list.push(r);
      byCid.set(cid, list);
    }
    if (r.IPAddress) {
      const list = rowsByIp.get(r.IPAddress) || [];
      list.push(r);
      rowsByIp.set(r.IPAddress, list);
    }
  }

  const byEventId = new Map();
  const groups = new Map();
  const counts = { attacker: 0, suspected: 0, benign: 0, unknown: 0 };

  for (const leg of rows) {
    if (!isAttributableLeg(leg) || !leg.EventId) continue;
    const evidence = [];
    if (leg.IPAddress && maliciousSet.has(leg.IPAddress)) evidence.push(EVIDENCE.LEG_IP_MALICIOUS);
    if (isUrlShapedApp(leg)) evidence.push(EVIDENCE.URL_APP);

    const cid = txIdOf(leg);
    const placeholderId = !cid && lower(leg.CorrelationId) === PLACEHOLDER_CORRELATION_ID;
    const link = {
      kind: null, correlationId: cid, tier: null,
      neighbourEventId: '', neighbourTimeUtc: '', neighbourRequestType: '', deltaSeconds: null,
      sessionIds: [],
    };
    let unresolvedReason = null;
    let groupKey = '';

    if (cid) {
      const legMs = toMs(leg.EventTimeUTC);
      const tx = (byCid.get(cid) || [leg]).filter((r) => {
        if (r === leg) return true;
        if (legMs === null || !sameUser(leg, r, identityIds)) return false;
        const ms = toMs(r.EventTimeUTC);
        return ms !== null && Math.abs(ms - legMs) <= TX_LEG_WINDOW_SECONDS * 1000;
      });
      link.kind = 'transaction';
      link.sessionIds = [...new Set(tx.map((r) => r.SessionId).filter(Boolean))];
      groupKey = cid;
      if (tx.some((r) => r.IPAddress && maliciousSet.has(r.IPAddress))) evidence.push(EVIDENCE.TX_IP_MALICIOUS);
      if (hasLoginMultiIp(tx)) evidence.push(EVIDENCE.TX_LOGIN_MULTI_IP);
      if (link.sessionIds.some((s) => attackerSessions.has(s))) evidence.push(EVIDENCE.TX_ATTACKER_SESSION);
      if (link.sessionIds.length === 0) unresolvedReason = UNRESOLVED.TRANSACTION_WITHOUT_SESSION;
      if (!groups.has(groupKey)) {
        groups.set(groupKey, { kind: 'transaction', legIds: [], memberIds: (byCid.get(cid) || [leg]).map((r) => r.EventId).filter(Boolean) });
      }
    } else {
      const nb = findNeighbour(leg, rowsByIp);
      if (nb.status === 'linked') {
        const r = nb.pick.row;
        link.kind = 'neighbour';
        link.tier = nb.tier;
        link.neighbourEventId = r.EventId || '';
        link.neighbourTimeUtc = r.EventTimeUTC || '';
        link.neighbourRequestType = r.RequestType || '';
        link.deltaSeconds = nb.pick.delta;
        link.sessionIds = [r.SessionId];
        groupKey = r.EventId ? `nb:${r.EventId}` : '';
        if (groupKey && !groups.has(groupKey)) {
          groups.set(groupKey, { kind: 'neighbour', legIds: [], memberIds: [r.EventId] });
        }
      } else if (nb.status === 'ambiguous') {
        unresolvedReason = UNRESOLVED.NEIGHBOUR_AMBIGUOUS;
        link.sessionIds = nb.sessions;
      } else {
        unresolvedReason = placeholderId
          ? UNRESOLVED.PLACEHOLDER_CORRELATION_ID
          : UNRESOLVED.NO_CORRELATION_ID_NO_NEIGHBOUR;
      }
    }

    const neighbourOnAttackerSession = link.kind === 'neighbour'
      && link.sessionIds.some((s) => attackerSessions.has(s));

    let verdict;
    if (evidence.length > 0) verdict = 'attacker';
    else if (neighbourOnAttackerSession) verdict = 'suspected';
    else if (link.sessionIds.length > 0 && !unresolvedReason) verdict = 'benign';
    else verdict = 'unknown';
    // A leg with evidence is attributed however it was linked; its unresolved reason, if any,
    // is only a display note. A benign leg by construction has none.
    if (verdict !== 'unknown') unresolvedReason = verdict === 'attacker' ? unresolvedReason : null;

    if (groupKey) {
      const g = groups.get(groupKey);
      g.legIds.push(leg.EventId);
      if (g.kind === 'neighbour') g.memberIds.push(leg.EventId);
    }

    counts[verdict] += 1;
    byEventId.set(leg.EventId, {
      link,
      evidence,
      verdict,
      unresolvedReason,
      multipleSessions: link.sessionIds.length > 1,
      // The linked sessions that ARE the attacker's — the wording names only these, so a leg
      // whose transaction also carries a clean session never reads as both being the attacker's.
      attackerSessionIds: link.sessionIds.filter((s) => attackerSessions.has(s)),
      groupKey,
      capture: evidence.includes(EVIDENCE.URL_APP),
    });
  }

  return { byEventId, groups, counts };
}

const shortSid = (s) => (s && s.length > 13 ? `${s.slice(0, 13)}…` : (s || ''));
const listPhrase = (items) => (items.length <= 2
  ? items.join(' and ')
  : `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`);
const sessionPhrase = (ids) => (ids.length === 1
  ? `session ${shortSid(ids[0])}`
  : `sessions ${listPhrase(ids.map(shortSid))}`);
/** "was" for one session, "were" for several — the verb agrees with sessionPhrase(ids). */
const wasWere = (ids) => (ids.length === 1 ? 'was' : 'were');

/**
 * What to tell the analyst about one recovered leg — the wording for the row badge's tooltip
 * (`summary`) and the expanded-row banner (`detail`), plus its `tone`
 * ('red' | 'amber' | 'neutral').
 *
 * 🔴 Plan §2.6 (review finding R5): the old copy told the analyst on EVERY recovered leg that
 * "this pattern is common on adversary-in-the-middle token issuance", so 52 of the victim's own
 * MFA pushes carried an AiTM warning. Every message now opens with the neutral fact and then
 * says exactly what linked the leg — and "re-run the investigation" is offered only where a
 * re-run can actually help (a record from before the runbook carried transaction ids).
 * The phishing-kit (URL-shaped application) warning is separate and unchanged; for a capture
 * leg this points at it rather than repeating it.
 *
 * @param {object|null} v  one `byEventId` entry from analyzeRecoveredLegs
 * @returns {{ tone: string, summary: string, detail: string } | null}
 */
export function describeRecoveredLeg(v) {
  if (!v) return null;
  const lead = 'Microsoft logs multi-factor sign-in steps without a username.';
  const sess = v.link?.sessionIds || [];

  if (v.verdict === 'attacker') {
    if (v.capture) {
      const summary = 'Part of a sign-in through a phishing-kit application. Counted as attacker activity.';
      return { tone: 'red', summary, detail: `${lead} ${summary}` };
    }
    const reasons = [];
    if (v.evidence.includes(EVIDENCE.LEG_IP_MALICIOUS) || v.evidence.includes(EVIDENCE.TX_IP_MALICIOUS)) {
      reasons.push('its sign-in transaction ran from an address classified malicious');
    }
    if (v.evidence.includes(EVIDENCE.TX_LOGIN_MULTI_IP)) {
      reasons.push('its sign-in started from more than one address within seconds of each other, the pattern of a relaying proxy');
    }
    if (v.evidence.includes(EVIDENCE.TX_ATTACKER_SESSION)) {
      const att = v.attackerSessionIds?.length ? v.attackerSessionIds : sess;
      reasons.push(`it belongs to ${sessionPhrase(att)}, which ${wasWere(att)} also used from an address classified malicious`);
    }
    const summary = `This one is attributed to the attacker: ${reasons.join('; and ')}. Counted as attacker activity.`;
    return { tone: 'red', summary, detail: `${lead} ${summary}` };
  }

  if (v.verdict === 'suspected') {
    const summary = `This one is probably part of ${sessionPhrase(sess)} (matched by address and time, not by a transaction id), and that session was also used from a malicious address. Not counted — review it, or re-run the investigation to link it exactly.`;
    return { tone: 'amber', summary, detail: `${lead} ${summary}` };
  }

  if (v.verdict === 'benign') {
    let summary;
    if (v.link.kind === 'neighbour') {
      const what = v.link.neighbourRequestType || 'sign-in';
      const d = Number(v.link.deltaSeconds);
      const when = Number.isFinite(d) ? ` ${Math.abs(d)} s ${d < 0 ? 'earlier' : 'later'}` : '';
      summary = `This one is probably part of ${sessionPhrase(sess)} — matched to the ${what}${when} on the same address, not by a transaction id — which was not used from a malicious address. Not counted.`;
    } else {
      summary = `This one belongs to ${sessionPhrase(sess)}, which ${wasWere(sess)} not used from a malicious address. Not counted.`;
    }
    return { tone: 'neutral', summary, detail: `${lead} ${summary}` };
  }

  let summary;
  switch (v.unresolvedReason) {
    case UNRESOLVED.TRANSACTION_WITHOUT_SESSION:
      summary = 'No record of its sign-in transaction within 10 minutes carries a session, so it cannot be tied to one. Not counted.';
      break;
    case UNRESOLVED.PLACEHOLDER_CORRELATION_ID:
      summary = 'Microsoft recorded this step with a placeholder transaction id, and no sign-in on the same address within a minute carries a session, so it cannot be tied to one. Not counted.';
      break;
    case UNRESOLVED.NEIGHBOUR_AMBIGUOUS:
      summary = `It could belong to more than one session (${sess.map(shortSid).join(', ')}), so it is not attributed. Not counted.`;
      break;
    default:
      summary = 'No parent sign-in could be found. This record predates transaction ids — re-running the investigation may link it. Not counted.';
  }
  return { tone: 'neutral', summary, detail: `${lead} ${summary}` };
}

/** The verdict for one row, or null when the row is not an attributable leg. */
export const legVerdictOf = (analysis, e) => analysis?.byEventId?.get(e?.EventId)?.verdict || null;

/**
 * Should the Safe-IP pill leave this sign-in visible regardless of its address?
 *
 * `attacker`, `suspected` and `unknown` legs stay visible — the last two in the SAFE direction,
 * since nothing established them as the user's. A `benign` leg follows the ordinary IP rules:
 * it is the user's own MFA on the user's own session, so marking the address Safe hides it
 * (the handoff's §1 complaint was precisely that it could not).
 *
 * Without an analysis (e.g. before one is computed) this falls back to the shape predicate,
 * which keeps every leg visible — the pre-v1.188.0 behaviour, and the safe direction.
 */
export function isVisibleStandaloneLeg(e, analysis) {
  if (!isAttributableLeg(e)) return false;
  const v = legVerdictOf(analysis, e);
  if (!v) return true;
  return v !== 'benign';
}
